import { useCallback, useRef } from "react";

import { type BaseObject } from "../../../../evidence/base";
import { useSettingsContext } from "../../../../settings/context";
import { type DataTablePreferenceProfileSettingsType } from "../../components/data-table-preferences/types";
import { useColumnFilter } from "./column-filter";
import { useColumnOrder } from "./column-order";
import { useColumnPinning } from "./column-pinning";
import { useColumnSizing } from "./column-sizing";
import { useVisibility } from "./column-visibility";
import { useGlobalFilter } from "./global-filter";
import { usePagination } from "./pagination";
import { useSelection } from "./selection";
import { useSort } from "./sort";
import {
  type UseDataTableStateParams,
  type UseDataTableStateResult,
} from "./types";

export function useDataTableState<TTableData extends BaseObject>({
  id,
  version = 1,
  defaultGlobalFilter,
  defaultVisibility,
  defaultColumnOrder,
  defaultPagination,
  defaultSorting,
  defaultColumnFilters,
}: UseDataTableStateParams): UseDataTableStateResult<TTableData> {
  const { preferences } = useSettingsContext();

  const profileClearedRef = useRef(false);

  const clearTableProfile = async () => {
    if (profileClearedRef.current) {
      return;
    }

    profileClearedRef.current = true;

    await preferences.set({
      key: id,
      preferences: { version, profile: null },
    });

    await preferences.refresh();
  };

  const { globalFilter, setGlobalFilter } = useGlobalFilter({
    defaultGlobalFilter,
  });
  const { columnFilters, setColumnFilters } = useColumnFilter({
    preferenceGroupKey: id,
    version,
    defaultColumnFilters,
  });
  const { columnSizing, setColumnSizing } = useColumnSizing({
    preferenceGroupKey: id,
    version,
  });
  const { columnVisibility, setColumnVisibility } = useVisibility({
    preferenceGroupKey: id,
    version,
    defaultVisibility,
  });
  const { columnOrder, setColumnOrder, sensors, handleDragEnd } =
    useColumnOrder({
      preferenceGroupKey: id,
      version,
      defaultColumnOrder,
    });
  const { columnPinning, setColumnPinning } = useColumnPinning({
    preferenceGroupKey: id,
    version,
  });
  const { rowSelection, setRowSelection } = useSelection();
  const { pagination, setPagination } = usePagination({
    defaultPagination,
  });
  const { sorting, setSorting } = useSort({
    preferenceGroupKey: id,
    version,
    defaultSorting,
  });

  const state = {
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    columnSizing,
    columnOrder,
    columnPinning,
    rowSelection,
    globalFilter,
  };

  const applyProfile = useCallback(
    (settings?: DataTablePreferenceProfileSettingsType) => {
      if (settings) {
        setColumnFilters(settings.columnFilters ?? []);
        setColumnOrder(settings.columnOrder ?? []);
        setColumnPinning(settings.columnPinning ?? {});
        setColumnSizing(settings.columnSizing ?? {});
        setColumnVisibility(settings.columnVisibility ?? {});
        setSorting(settings.sorting ?? []);
      }

      profileClearedRef.current = false;
    },
    [
      setColumnFilters,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      setColumnVisibility,
      setSorting,
    ],
  );

  return {
    state,
    stateHandlers: {
      onSortingChange: (updater) => {
        setSorting(updater);
        void clearTableProfile();
      },
      onColumnFiltersChange: (updater) => {
        setColumnFilters(updater);
        void clearTableProfile();
      },
      onColumnVisibilityChange: (updater) => {
        setColumnVisibility(updater);
        void clearTableProfile();
      },
      onColumnSizingChange: (updater) => {
        setColumnSizing(updater);
        void clearTableProfile();
      },
      onColumnOrderChange: (updater) => {
        setColumnOrder(updater);
        void clearTableProfile();
      },
      onColumnPinningChange: (updater) => {
        setColumnPinning(updater);
        void clearTableProfile();
      },
      onGlobalFilterChange: setGlobalFilter,
      onRowSelectionChange: setRowSelection,
      onPaginationChange: setPagination,
    },
    dndContext: {
      sensors,
      handleDragEnd,
    },
    initialState: {
      columnFilters: defaultColumnFilters,
      columnVisibility: defaultVisibility,
      globalFilter: defaultGlobalFilter,
      columnOrder: defaultColumnOrder,
      pagination: defaultPagination,
      sorting: defaultSorting,
    },
    applyProfile,
  };
}
